import React, { useRef, useState, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import PressableScale from './motion/PressableScale';
import { View, Text, Platform, Animated, Dimensions } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '../context/NavigationContext';
import { useTicker } from '../context/TickerContext';
import { useBackgroundQuery } from '../context/BackgroundQueryContext';
import { useAuth } from '../context/AuthContext';
import { styles } from './gameTickerStyles';
import { TickerChrome, SkeletonTickerItem, TickerItem, getGameStatus, parseDate, formatGameTime, formatPeriod } from './gameTickerParts';
import GamePopover from './GamePopover';
import { isLoadingGameLines, loadGameLines, peekGameLines, precomputedLines, prefetchGameLines } from '../services/gameLines';
import { useReducedMotion, prefersReducedMotion } from '../utils/motion';
import {
  sortByLeague, buildLeagueSections, adjacentSectionIndex, sectionIndexAtOffset,
  measureRowOffsets, reanchorOffset, planLeagueJump, wrapOffset, easeOutQuint, copiesNeeded,
} from '../utils/tickerNavigation';

const { width: screenWidth } = Dimensions.get('window');
const IS_WEB = Platform.OS === 'web';

/** px/s at 1x. Display speeds 1x/2x/3x map to 1/3/5x. */
const BASE_SPEED = 40;
const SPEED_MULTIPLIER = { 1: 1, 2: 3, 3: 5 };
/** A jump lands a section this far in from the left edge so its league badge clears the fade. */
const LAND_INSET = 28;
/** Re-read row geometry this often while running (fonts, logos, live scores change widths). */
const MEASURE_EVERY_MS = 1500;
/** Fade colour = the ticker's own background, so rows dissolve into it. */
const FADE_SOLID = 'rgba(8,8,11,1)';
const FADE_CLEAR = 'rgba(8,8,11,0)';

/** Game card: hover this long before it opens; leave this long before it closes (time to reach the card). */
const CARD_OPEN_MS = 120;
const CARD_CLOSE_MS = 180;
const CARD_ID = 'game-ticker-card';
/** A press within this long of a touch is a tap: it toggles the card instead of searching. */
const TOUCH_TAP_MS = 800;
/**
 * While the card is already open, moving it to another game reads that game's lines
 * only once it has stayed there this long (a sweep across the strip reads nothing).
 * A card opening fresh (after the hover delay, a keyboard focus or a tap) reads at once.
 */
const LINES_FETCH_DELAY_MS = 250;

const focusEl = (el) => { try { el?.focus?.(); } catch { /* detached */ } };
/** contains() for event targets that may be window / document. */
const within = (root, t) => !!(root && t && typeof t.nodeType === 'number' && root.contains && root.contains(t));

const statusOrder = { live: 0, soon: 1, upcoming: 2 };
const leagueLabel = (league) => (league ? league.toUpperCase() : 'OTHER');
const fixtureKey = (f) => String(f.fixture_id || f.id || `${f.home_team}-${f.away_team}-${f.start_date?._seconds ?? f.start_date}`);

/** Same rule as the old 280/80 estimates, used only until the first real measurement. */
function estimateGeometry(count, sections, ids) {
  const starts = new Set(sections.map((s) => s.startIndex));
  const rowOffsets = [];
  let x = 0;
  for (let i = 0; i < count; i++) {
    rowOffsets.push(x);
    x += 280 + (starts.has(i) ? 80 : 0);
  }
  return { rowOffsets, loopWidth: x, ids, estimated: true };
}

/** Native fallback: onLayout fires on position changes there (unlike react-native-web). */
function geometryFromLayouts(layouts, n) {
  if (!layouts || layouts.length < n + 1) return null;
  for (let i = 0; i <= n; i++) if (!layouts[i] || !(layouts[i].width > 0)) return null;
  const base = layouts[0].x;
  const loopWidth = layouts[n].x - base;
  if (!(loopWidth > 0)) return null;
  return { rowOffsets: layouts.slice(0, n).map((l) => l.x - base), loopWidth };
}

function sameGeometry(a, b) {
  if (!a || !b || a.estimated !== b.estimated) return false;
  if (Math.abs(a.loopWidth - b.loopWidth) > 0.5) return false;
  if (a.ids.length !== b.ids.length || a.rowOffsets.length !== b.rowOffsets.length) return false;
  for (let i = 0; i < a.ids.length; i++) {
    if (a.ids[i] !== b.ids[i] || Math.abs(a.rowOffsets[i] - b.rowOffsets[i]) > 0.5) return false;
  }
  return true;
}

/**
 * GameTicker — looping strip of games grouped by league, with "‹ LEAGUE" /
 * "LEAGUE ›" skip buttons.
 *
 * One rAF loop owns all movement: cruising (eases to a stop on hover/pause)
 * and seeking (a league jump). Rapid clicks re-target the running seek instead
 * of being dropped. Row offsets are read from the DOM when needed, so jumps
 * land on the real section start even after the 5-minute fixture refresh.
 * The strip renders 2+ identical copies and always shows wrapOffset(offset),
 * so the loop seam never shows blank space in either direction.
 */
const GameTicker = ({ fixtures = [], isVisible = true, onToggle, isLoading = false, isLoadingMore = false, isCollapsed = false, onCollapseToggle }) => {
  const { navigateTo, currentScreen, setRoutePayloadDirect } = useNavigation();
  const { tickerNameFormat, showTickerSGPIndicator, tickerSGPOnly, sgpCacheStatus, showLiveScores, liveScores, loadProgress, referenceTime } = useTicker();
  const { addBackgroundQuery, canAddBackgroundQuery } = useBackgroundQuery();
  const { userProfile } = useAuth();
  const reduced = useReducedMotion();

  // Check if background query mode is enabled
  const backgroundQueryMode = userProfile?.preferences?.backgroundQueryMode === true;

  // Reduced motion: the strip starts still; the speed button starts it.
  const [isPaused, setIsPaused] = useState(() => prefersReducedMotion());
  const [tickerSpeed, setTickerSpeed] = useState(1); // Display: 1x, 2x, 3x → Actual: 1x, 3x, 5x
  useEffect(() => { if (reduced) setIsPaused(true); }, [reduced]);

  // Sort fixtures: one contiguous run per league, then LIVE > SOON > UPCOMING, then time.
  // Filter out finished games and SGP-only if enabled.
  const sortedFixtures = useMemo(() => {
    let filtered = fixtures.filter((f) => getGameStatus(f.start_date, referenceTime).status !== 'ended');
    if (tickerSGPOnly && Object.keys(sgpCacheStatus).length > 0) {
      filtered = filtered.filter((f) => sgpCacheStatus[f.fixture_id || f.id] === true);
    }
    const time = (f) => parseDate(f.start_date)?.getTime() || 0;
    return sortByLeague(filtered, (f) => f.league, (a, b) => {
      const sa = statusOrder[getGameStatus(a.start_date, referenceTime).status] ?? 3;
      const sb = statusOrder[getGameStatus(b.start_date, referenceTime).status] ?? 3;
      return sa !== sb ? sa - sb : time(a) - time(b);
    });
  }, [fixtures, tickerSGPOnly, sgpCacheStatus, referenceTime]);

  const n = sortedFixtures.length;
  const ids = useMemo(() => sortedFixtures.map(fixtureKey), [sortedFixtures]);
  const fixturesKey = ids.join('|');
  const sections = useMemo(() => buildLeagueSections(sortedFixtures, (f) => f.league), [sortedFixtures]);

  // ── Geometry ─────────────────────────────────────────────────────────────
  const [viewportWidth, setViewportWidth] = useState(screenWidth);
  const [measuredLoopWidth, setMeasuredLoopWidth] = useState(0);
  const copies = copiesNeeded(viewportWidth, measuredLoopWidth || n * 280);

  const stripRef = useRef(null);      // web: the row container (DOM node)
  const wrapperRef = useRef(null);
  const nativeLayoutsRef = useRef([]);
  const geomRef = useRef(null);       // { rowOffsets, loopWidth, ids, sectionOffsets, estimated? }
  const scrollX = useRef(new Animated.Value(0)).current; // native only
  const live = useRef({ ids, sections, n }).current;
  live.ids = ids; live.sections = sections; live.n = n;

  /** All motion state; the rAF loop and the buttons share it. */
  const motionRef = useRef({ offset: 0, velocity: 0, seek: null, sectionIndex: 0 });
  const pausedRef = useRef(isPaused);
  const hoverRef = useRef(false);
  const cardOpenRef = useRef(false); // an open game card holds the strip still
  const closeCardRef = useRef(() => {});
  const speedRef = useRef(1);
  const reducedRef = useRef(reduced);
  pausedRef.current = isPaused;
  speedRef.current = SPEED_MULTIPLIER[tickerSpeed] || 1;
  reducedRef.current = reduced;

  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const renderedSectionRef = useRef(0);

  const applyOffset = useCallback((offset) => {
    const W = geomRef.current?.loopWidth || 0;
    const x = -wrapOffset(offset, W);
    if (IS_WEB) {
      const node = stripRef.current;
      if (node && node.style) node.style.transform = `translate3d(${x}px,0,0)`;
    } else {
      scrollX.setValue(x);
    }
  }, [scrollX]);

  const publishSection = useCallback((idx) => {
    const m = motionRef.current;
    if (idx !== m.sectionIndex) {
      m.sectionIndex = idx;
      setCurrentSectionIndex(idx);
    }
  }, []);

  /**
   * Reads row geometry now (DOM on web, onLayout on native). When it changed,
   * keeps the same game at the left edge and lands any running jump on the
   * target's new position. Returns the geometry in use.
   */
  const measureGeometry = useCallback(() => {
    const { ids: curIds, sections: secs, n: count } = live;
    if (count === 0) { geomRef.current = null; return null; }
    let m = null;
    if (IS_WEB) {
      const node = stripRef.current;
      if (node && node.children) m = measureRowOffsets(node.children, count);
    } else {
      m = geometryFromLayouts(nativeLayoutsRef.current, count);
    }
    const next = m ? { ...m, ids: curIds } : (geomRef.current && !geomRef.current.estimated && geomRef.current.ids === curIds
      ? geomRef.current
      : estimateGeometry(count, secs, curIds));
    next.sectionOffsets = secs.map((s) => next.rowOffsets[s.startIndex] ?? 0);
    const prev = geomRef.current;
    if (prev && sameGeometry(prev, next)) {
      prev.sectionOffsets = next.sectionOffsets;
      return prev;
    }
    const motion = motionRef.current;
    if (motion.seek && prev) {
      // Geometry moved under a running jump: land on the target's new spot.
      // Find the target league in the NEW section list (a refresh can drop a league before it).
      const targetIdx = secs.findIndex((sec) => sec.league === motion.seek.league);
      motion.seek = null;
      if (targetIdx >= 0) motion.offset = next.sectionOffsets[targetIdx] - LAND_INSET;
      else motion.offset = reanchorOffset(motion.offset, prev, next);
    } else {
      motion.offset = reanchorOffset(motion.offset, prev, next);
    }
    geomRef.current = next;
    if (!next.estimated) setMeasuredLoopWidth((w) => (Math.abs(w - next.loopWidth) > 1 ? next.loopWidth : w));
    applyOffset(motion.offset);
    const W = next.loopWidth;
    if (!motion.seek) publishSection(sectionIndexAtOffset(motion.offset + LAND_INSET, next.sectionOffsets, W));
    return next;
  }, [live, applyOffset, publishSection]);

  // Re-measure synchronously after any change to the rows, before paint, so a
  // refresh never flashes the old offset over new rows.
  useLayoutEffect(() => {
    nativeLayoutsRef.current.length = n + 1;
    measureGeometry();
  }, [fixturesKey, copies, viewportWidth, n, measureGeometry]);

  // ── The one motion loop ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isVisible || n === 0) return undefined;
    const now0 = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
    let last = now0();
    let lastMeasure = last;
    let frame;
    const tick = () => {
      const now = now0();
      const dt = Math.min(0.1, (now - last) / 1000); // a background tab must not teleport
      last = now;
      if (now - lastMeasure > MEASURE_EVERY_MS) { lastMeasure = now; measureGeometry(); }
      const g = geomRef.current;
      const m = motionRef.current;
      if (g && g.loopWidth > 0) {
        let moved = false;
        if (m.seek) {
          const t = (now - m.seek.start) / m.seek.duration;
          if (t >= 1) {
            m.offset = wrapOffset(m.seek.to, g.loopWidth);
            m.seek = null;
          } else {
            m.offset = m.seek.from + (m.seek.to - m.seek.from) * easeOutQuint(t);
          }
          moved = true;
        } else {
          // Ease toward cruise speed: hover/pause glide to a stop instead of freezing.
          const target = pausedRef.current || hoverRef.current || cardOpenRef.current ? 0 : speedRef.current;
          m.velocity += (target - m.velocity) * Math.min(1, dt * 7);
          if (Math.abs(m.velocity) < 0.002 && target === 0) m.velocity = 0;
          if (m.velocity !== 0) {
            m.offset = wrapOffset(m.offset + BASE_SPEED * m.velocity * dt, g.loopWidth);
            moved = true;
          }
        }
        if (moved) applyOffset(m.offset);
        publishSection(m.seek ? m.seek.targetIndex : sectionIndexAtOffset(m.offset + LAND_INSET, g.sectionOffsets, g.loopWidth));
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [isVisible, n, measureGeometry, applyOffset, publishSection]);

  // ── League skip ──────────────────────────────────────────────────────────
  const jump = useCallback((direction) => {
    closeCardRef.current(); // the game it points at is about to slide away
    const g = measureGeometry(); // fresh offsets at click time
    if (!g || !(g.loopWidth > 0)) return;
    const m = motionRef.current;
    // Rapid clicks chain from the pending target, not from what is on screen.
    const base = m.seek ? m.seek.targetIndex : renderedSectionRef.current;
    const targetIndex = adjacentSectionIndex(live.sections, base, direction);
    if (targetIndex < 0) return;
    const target = g.sectionOffsets[targetIndex] - LAND_INSET;
    const plan = planLeagueJump(m.offset, target, g.loopWidth, direction, { reduced: reducedRef.current });
    m.velocity = 0; // land, then ease back up to cruise
    if (plan.duration === 0) {
      m.seek = null;
      m.offset = wrapOffset(plan.to, g.loopWidth);
      applyOffset(m.offset);
    } else {
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      m.seek = { ...plan, start: now, targetIndex, league: live.sections[targetIndex].league };
      m.offset = plan.from;
    }
    publishSection(targetIndex);
  }, [measureGeometry, live, applyOffset, publishSection]);

  const skipToNextLeague = useCallback(() => jump('next'), [jump]);
  const skipToPreviousLeague = useCallback(() => jump('prev'), [jump]);

  // Horizontal trackpad / shift-wheel scrubs the strip directly. Attached
  // natively so preventDefault works (React's wheel listener is passive) and
  // a sideways swipe doesn't trigger browser back/forward.
  useEffect(() => {
    if (!IS_WEB) return undefined;
    const node = wrapperRef.current;
    if (!node || !node.addEventListener) return undefined;
    const onWheel = (e) => {
      const dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : (e.shiftKey ? e.deltaY : 0);
      if (!dx) return;
      e.preventDefault();
      closeCardRef.current();
      const g = geomRef.current;
      if (!g || !(g.loopWidth > 0)) return;
      const m = motionRef.current;
      m.seek = null;
      m.offset = wrapOffset(m.offset + dx, g.loopWidth);
      applyOffset(m.offset);
      publishSection(sectionIndexAtOffset(m.offset + LAND_INSET, g.sectionOffsets, g.loopWidth));
    };
    // Focusing a row scrolls an overflow:hidden box; the strip moves only by transform.
    const onScroll = () => { if (node.scrollLeft) node.scrollLeft = 0; };
    const onFocusIn = () => { hoverRef.current = true; };
    const onFocusOut = (e) => { if (!node.contains(e.relatedTarget)) hoverRef.current = false; };
    node.addEventListener('wheel', onWheel, { passive: false });
    node.addEventListener('scroll', onScroll);
    node.addEventListener('focusin', onFocusIn);
    node.addEventListener('focusout', onFocusOut);
    return () => {
      node.removeEventListener('wheel', onWheel);
      node.removeEventListener('scroll', onScroll);
      node.removeEventListener('focusin', onFocusIn);
      node.removeEventListener('focusout', onFocusOut);
    };
  }, [n > 0, isVisible, applyOffset, publishSection]);

  const sectionCount = sections.length;
  const safeSection = Math.min(currentSectionIndex, Math.max(0, sectionCount - 1));
  renderedSectionRef.current = safeSection; // a click goes where the visible label says
  const nextIdx = adjacentSectionIndex(sections, safeSection, 'next');
  const prevIdx = adjacentSectionIndex(sections, safeSection, 'prev');
  const nextLeague = nextIdx >= 0 ? leagueLabel(sections[nextIdx].league) : null;
  const previousLeague = prevIdx >= 0 ? leagueLabel(sections[prevIdx].league) : null;

  const handleGamePress = async (fixture) => {
    const fixtureId = fixture.fixture_id || fixture.id;
    const query = `${fixture.home_team} vs ${fixture.away_team}`;
    
    if (!fixtureId) {
      console.warn('[GameTicker] ⚠️ No fixtureId for ticker click:', { home: fixture.home_team, away: fixture.away_team, keys: Object.keys(fixture) });
    } else {
      console.warn('[GameTicker] 🎯 Ticker click fixtureId:', fixtureId, fixture.home_team, 'vs', fixture.away_team);
    }
    
    // ⭐ If SGP-only mode is enabled, use SGP search behavior
    if (tickerSGPOnly) {
      // Navigate to SgpFlow with specific fixture context and force SGP mode
      const payload = { 
        query,
        fixtureId,
        mode: 'sgp',
        fromCachedSGP: true, // Flag to indicate we're viewing cached SGPs
        fromTicker: true, // ⭐ Track that this query came from ticker click
        league: fixture.league,
      };
      
      if (currentScreen === 'SgpFlow') {
        try { 
          window.sessionStorage.setItem('sgp:lastQuery', query);
          window.sessionStorage.setItem('sgp:fixtureId', fixtureId);
          window.sessionStorage.setItem('sgp:fromCached', 'true');
        } catch {}
        if (setRoutePayloadDirect) {
          setRoutePayloadDirect(payload);
        }
      } else {
        navigateTo('SgpFlow', { payload });
      }
      return;
    }

    // ⭐ Regular game search behavior - NOW PASSES fixtureId for exact matchup filtering
    const payload = { 
      query,
      fixtureId, // Pass fixture ID for exact game matching
      league: fixture.league,
      sport: fixture.sport,
      fromTicker: true, // ⭐ Track that this query came from ticker click
    };
    
    // Check if background query mode is enabled
    if (backgroundQueryMode && canAddBackgroundQuery) {
      // Add to background queue - no page navigation
      await addBackgroundQuery(query, {
        league: fixture.league,
        sport: fixture.sport,
        fromTicker: true,
        fixtureId, // ⭐ CRITICAL: Pass fixtureId for exact matchup filtering!
      });
      return;
    }
    
    // If already on SgpFlow, directly update the payload to trigger a new query
    if (currentScreen === 'SgpFlow') {
      // Store query in session storage for the effect to pick up
      try { 
        window.sessionStorage.setItem('sgp:lastQuery', query); 
      } catch {}
      // Directly update route payload to trigger the useEffect
      if (setRoutePayloadDirect) {
        setRoutePayloadDirect(payload);
      }
    } else {
      navigateTo('SgpFlow', { payload });
    }
  };
  
  // ⭐ Handle SGP badge click - navigate directly to SGP view for this fixture
  const handleSGPPress = async (fixture) => {
    const fixtureId = fixture.fixture_id || fixture.id;
    const query = `${fixture.home_team} vs ${fixture.away_team} SGPs`;
    
    // Navigate to SgpFlow with specific fixture context and force SGP mode
    const payload = { 
      query,
      fixtureId,
      mode: 'sgp',
      fromCachedSGP: true,
      league: fixture.league,
      sport: fixture.sport,
    };
    
    // Respect background query mode — same as regular ticker clicks
    if (backgroundQueryMode && canAddBackgroundQuery) {
      await addBackgroundQuery(query, {
        league: fixture.league,
        sport: fixture.sport,
        fromTicker: true,
        fixtureId,
        mode: 'sgp',
        fromCachedSGP: true,
      });
      return;
    }

    // If already on SgpFlow, directly update the payload
    if (currentScreen === 'SgpFlow') {
      try { 
        window.sessionStorage.setItem('sgp:lastQuery', query);
        window.sessionStorage.setItem('sgp:fixtureId', fixtureId);
        window.sessionStorage.setItem('sgp:fromCached', 'true');
      } catch {}
      if (setRoutePayloadDirect) {
        setRoutePayloadDirect(payload);
      }
    } else {
      navigateTo('SgpFlow', { payload });
    }
  };

  // 🔴 Handle LIVE badge click - query live odds for this in-progress fixture
  const handleLivePress = async (fixture) => {
    const fixtureId = fixture.fixture_id || fixture.id;
    const query = `${fixture.home_team} vs ${fixture.away_team}`;

    const payload = {
      query,
      fixtureId,
      isLive: true,
      fromTicker: true,
      league: fixture.league,
      sport: fixture.sport,
    };

    // Respect background query mode — same as regular and SGP ticker clicks
    if (backgroundQueryMode && canAddBackgroundQuery) {
      await addBackgroundQuery(query, {
        league: fixture.league,
        sport: fixture.sport,
        fromTicker: true,
        fixtureId,
        isLive: true,
      });
      return;
    }

    if (currentScreen === 'SgpFlow') {
      try {
        window.sessionStorage.setItem('sgp:lastQuery', query);
        window.sessionStorage.setItem('sgp:fixtureId', fixtureId);
      } catch {}
      if (setRoutePayloadDirect) {
        setRoutePayloadDirect(payload);
      }
    } else {
      navigateTo('SgpFlow', { payload });
    }
  };
  
  // Handlers change identity every render; rows read them through a ref so the
  // (large) row list only re-renders when its data changes.
  const handlersRef = useRef(null);
  handlersRef.current = { game: handleGamePress, sgp: handleSGPPress, live: handleLivePress };
  const onRowSGP = useCallback((f) => { closeCardRef.current(); handlersRef.current.sgp(f); }, []);
  const onRowLive = useCallback((f) => { closeCardRef.current(); handlersRef.current.live(f); }, []);

  // ── Game card (web): hover / keyboard focus / tap opens it ─────────────────
  // Mirrors InjuryTicker's player card. Lines load lazily from the odds cache
  // (services/gameLines: 2-minute per-fixture cache, shared in-flight request).
  const [card, setCard] = useState(null); // { key, fixture, rect, el }
  const cardStateRef = useRef(null);
  cardStateRef.current = card;
  cardOpenRef.current = !!card;
  const cardElRef = useRef(null);
  const firstActionRef = useRef(null);
  const lastTouchRef = useRef(0);
  const pointerTypeRef = useRef(null); // 'touch' | 'mouse' | 'pen' from the last pointerdown on a game
  // Programmatic focus back onto a game (Escape, Shift+Tab, Tab past the last game) must not reopen its card.
  const suppressFocusOpenRef = useRef(false);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const clearCardTimers = useCallback(() => {
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  }, []);
  useEffect(() => clearCardTimers, [clearCardTimers]);

  const closeCard = useCallback(() => { clearCardTimers(); setCard(null); }, [clearCardTimers]);
  closeCardRef.current = closeCard;

  const openCard = useCallback((key, fixture, el) => {
    clearCardTimers();
    const rect = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    if (!rect) return;
    // A fresh open has already passed the hover delay (or is a focus / tap): start the
    // lines read now, unless the card can paint from precomputed or cached lines.
    // Moving an open card between games keeps the fetch delay below.
    if (!cardStateRef.current) prefetchGameLines(fixture);
    setCard({ key, fixture, rect, el });
  }, [clearCardTimers]);
  const onCardHoverIn = useCallback((key, fixture, el) => {
    // A tap's compat mouseenter would race the tap toggle; touch uses the tap alone.
    if (Date.now() - lastTouchRef.current < TOUCH_TAP_MS) return;
    clearCardTimers();
    if (cardStateRef.current) { openCard(key, fixture, el); return; } // already open: move instantly
    openTimer.current = setTimeout(() => openCard(key, fixture, el), CARD_OPEN_MS);
  }, [openCard, clearCardTimers]);
  const scheduleCardClose = useCallback(() => {
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => { closeTimer.current = null; setCard(null); }, CARD_CLOSE_MS);
  }, []);
  const keepCardOpen = useCallback(() => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  }, []);
  // Keyboard focus opens the card; a mouse click (RNW focuses on mousedown) is left to hover/press.
  const onCardFocus = useCallback((key, fixture, el, e) => {
    if (suppressFocusOpenRef.current) { suppressFocusOpenRef.current = false; return; }
    const t = e?.target || e?.nativeEvent?.target;
    let keyboard = true;
    try { keyboard = !t?.matches || t.matches(':focus-visible'); } catch { keyboard = true; }
    if (keyboard) openCard(key, fixture, el);
    else keepCardOpen();
  }, [openCard, keepCardOpen]);
  const onCardBlur = useCallback((e) => {
    const next = e?.relatedTarget || e?.nativeEvent?.relatedTarget;
    if (within(cardElRef.current, next)) return;
    scheduleCardClose();
  }, [scheduleCardClose]);
  const onCardContainerBlur = useCallback((e) => {
    const next = e?.relatedTarget || e?.nativeEvent?.relatedTarget;
    if (within(cardElRef.current, next)) return;
    if (next && cardStateRef.current?.el === next) return;
    scheduleCardClose();
  }, [scheduleCardClose]);

  /** Tab from the open game moves into its card (the card lives at the end of <body>). */
  const onChipKeyDown = useCallback((e, key) => {
    if (e.key !== 'Tab' || e.shiftKey) return;
    const open = cardStateRef.current;
    if (!open || open.key !== key || !firstActionRef.current) return;
    e.preventDefault();
    keepCardOpen();
    focusEl(firstActionRef.current);
  }, [keepCardOpen]);
  /** Shift+Tab off the first action returns to the game; Tab off the last goes on to the next game. */
  const onCardKeyDown = useCallback((e) => {
    const key = e?.key || e?.nativeEvent?.key;
    if (key !== 'Tab') return;
    const root = cardElRef.current;
    const open = cardStateRef.current;
    if (!root || !open || typeof document === 'undefined') return;
    const items = Array.from(root.querySelectorAll('[role="button"]'));
    if (!items.length) return;
    const active = document.activeElement;
    const shift = e.shiftKey || e.nativeEvent?.shiftKey;
    if (shift && active === items[0]) {
      // Back to the game; its card stays open (focus on the game keeps it).
      e.preventDefault();
      keepCardOpen();
      suppressFocusOpenRef.current = true;
      focusEl(open.el);
      suppressFocusOpenRef.current = false;
    } else if (!shift && active === items[items.length - 1]) {
      e.preventDefault();
      const chips = Array.from(stripRef.current?.querySelectorAll?.('[data-game-chip="true"]') || []);
      const next = chips[chips.indexOf(open.el) + 1];
      closeCard();
      if (next) {
        focusEl(next); // opens the next game's card, as Tab between games does
      } else {
        // Last game: land back on it with the card closed, so the next Tab leaves the strip.
        suppressFocusOpenRef.current = true;
        focusEl(open.el);
        suppressFocusOpenRef.current = false;
      }
    }
  }, [closeCard, keepCardOpen]);

  const onChipPointerDown = useCallback((type) => { pointerTypeRef.current = type || null; }, []);

  /** Mouse / keyboard press searches (unchanged); a touch tap toggles the card first. */
  const onRowPress = useCallback((fixture, key, el) => {
    // pointerType when the browser reports it (a long press is still a tap); else a recent touchstart.
    const touch = pointerTypeRef.current === 'touch' || Date.now() - lastTouchRef.current < TOUCH_TAP_MS;
    pointerTypeRef.current = null; // a later keyboard Enter is not a tap
    const tap = IS_WEB && touch;
    if (tap && el) {
      if (cardStateRef.current?.key === key) closeCard();
      else openCard(key, fixture, el);
      return;
    }
    closeCard();
    handlersRef.current.game(fixture);
  }, [openCard, closeCard]);

  const cardAction = (kind) => () => {
    const open = cardStateRef.current;
    if (!open) return;
    closeCard();
    handlersRef.current[kind](open.fixture);
  };

  // Escape, a tap outside, or the page scrolling closes the card.
  useEffect(() => {
    if (!IS_WEB || !card || typeof document === 'undefined') return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const inside = within(cardElRef.current, document.activeElement);
      const anchor = cardStateRef.current?.el;
      closeCard();
      if (inside) {
        suppressFocusOpenRef.current = true;
        focusEl(anchor);
        suppressFocusOpenRef.current = false;
      }
    };
    const onDown = (e) => {
      if (within(cardElRef.current, e.target)) return;
      if (e.target?.closest?.('[data-game-chip]')) return; // games toggle / search themselves
      closeCard();
    };
    const onScroll = (e) => {
      if (within(cardElRef.current, e.target)) return; // the card's own overflow
      // Focusing a game nudges the ticker's overflow box (reset to 0 above); the strip moves by transform.
      if (within(wrapperRef.current, e.target)) return;
      // Only a scroll that moves the game (the page, or a box holding the ticker) closes it;
      // the injury ticker auto-scrolls every frame and must not.
      const t = e.target;
      const page = !t || t === window || t === document || t === document.documentElement || t === document.body;
      if (!page && !within(t, cardStateRef.current?.el)) return;
      closeCard();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown, true);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', closeCard);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', closeCard);
    };
  }, [card, closeCard]);

  // Lines: the precomputed value when current (odds_cache topLines), else fetched
  // per fixture (already in flight when the card opened fresh). A late answer for a
  // game the card has since left is dropped (the cache keeps it for the next open).
  const cardFixtureId = card ? String(card.fixture.fixture_id || card.fixture.id || '') : '';
  const [lines, setLines] = useState({ fixtureId: '', status: 'idle', value: null });
  useEffect(() => {
    if (!cardFixtureId) return undefined;
    const fixture = cardStateRef.current?.fixture;
    const cached = peekGameLines(fixture) || precomputedLines(fixture);
    if (cached) { setLines({ fixtureId: cardFixtureId, status: 'ready', value: cached }); return undefined; }
    setLines({ fixtureId: cardFixtureId, status: 'loading', value: null });
    let current = true;
    const load = () => loadGameLines(fixture).then(
      (value) => { if (current) setLines({ fixtureId: cardFixtureId, status: 'ready', value }); },
      () => { if (current) setLines({ fixtureId: cardFixtureId, status: 'error', value: null }); },
    );
    if (isLoadingGameLines(fixture)) { load(); return () => { current = false; }; } // join the read already running
    const timer = setTimeout(load, LINES_FETCH_DELAY_MS);
    return () => { current = false; clearTimeout(timer); };
  }, [cardFixtureId]);

  const activeCardKey = card ? card.key : null;
  const rows = useMemo(() => {
    if (n === 0) return null;
    const out = [];
    const lg = (i) => (sortedFixtures[(i + n) % n].league || '').toLowerCase();
    for (let copy = 0; copy < copies; copy++) {
      for (let i = 0; i < n; i++) {
        const fixture = sortedFixtures[i];
        const fixtureId = fixture.fixture_id || fixture.id;
        const index = copy * n + i;
        // Modular neighbours so every copy is identical (the seam stays seamless).
        const isFirstInLeague = i === 0 || lg(i) !== lg(i - 1);
        const isLastInLeague = i === n - 1 || lg(i) !== lg(i + 1);
        out.push(
          <TickerItem
            key={`${ids[i]}~${copy}`}
            fixture={fixture}
            index={index}
            nameFormat={tickerNameFormat}
            chipKey={`${ids[i]}~${copy}`}
            onGamePress={onRowPress}
            cardId={CARD_ID}
            cardActive={activeCardKey === `${ids[i]}~${copy}`}
            onCardHoverIn={IS_WEB ? onCardHoverIn : undefined}
            onCardHoverOut={scheduleCardClose}
            onCardFocus={onCardFocus}
            onCardBlur={onCardBlur}
            onCardKeyDown={onChipKeyDown}
            onCardPointerDown={onChipPointerDown}
            statusKey={getGameStatus(fixture.start_date, referenceTime).status}
            onSGPPress={onRowSGP}
            onLivePress={onRowLive}
            showSGPIndicator={showTickerSGPIndicator}
            hasCachedSGPs={sgpCacheStatus[fixtureId] === true}
            liveScore={showLiveScores && liveScores ? liveScores[fixtureId] : null}
            isFirstInLeague={isFirstInLeague}
            isLastInLeague={isLastInLeague}
            decorative={copy > 0}
            onRowLayout={!IS_WEB && index <= n ? (e) => {
              const { x, width } = e.nativeEvent.layout;
              nativeLayoutsRef.current[index] = { x, width };
            } : undefined}
            referenceTime={referenceTime}
          />
        );
      }
    }
    return out;
  }, [sortedFixtures, ids, n, copies, tickerNameFormat, showTickerSGPIndicator, sgpCacheStatus, showLiveScores, liveScores, referenceTime, onRowSGP, onRowLive,
    activeCardKey, onRowPress, onCardHoverIn, scheduleCardClose, onCardFocus, onCardBlur, onChipKeyDown, onChipPointerDown]);

  const cycleSpeed = () => {
    if (isPaused) {
      setIsPaused(false);
      setTickerSpeed(1);
    } else if (tickerSpeed >= 3) {
      setIsPaused(true);
    } else {
      setTickerSpeed((prev) => prev + 1);
    }
  };
  const speedLabel = isPaused ? 'Ticker paused. Play at 1x' : tickerSpeed >= 3 ? `Ticker speed ${tickerSpeed}x. Pause` : `Ticker speed ${tickerSpeed}x. Speed up`;

  const collapseButton = onCollapseToggle ? (
    <PressableScale
      onPress={onCollapseToggle}
      style={styles.controlBtn}
      hoverStyle={styles.controlBtnHover}
      hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      role="button"
      aria-label={isCollapsed ? 'Expand game ticker' : 'Collapse game ticker'}
    >
      <Ionicons name={isCollapsed ? 'chevron-down' : 'chevron-up'} size={12} color="rgba(255,255,255,0.6)" />
    </PressableScale>
  ) : null;

  const fades = (
    <>
      <LinearGradient pointerEvents="none" colors={[FADE_SOLID, FADE_CLEAR]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={[styles.edgeFade, styles.leftFade]} />
      <LinearGradient pointerEvents="none" colors={[FADE_CLEAR, FADE_SOLID]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={[styles.edgeFade, styles.rightFade]} />
    </>
  );

  if (!isVisible) return null;

  // Show skeleton loading state
  if (isLoading && n === 0) {
    return (
      <View style={styles.container}>
        <TickerChrome>
          <View style={styles.gradientBg} />
          <View style={styles.controls}>
            <View style={[styles.speedToggleBtn, { opacity: 0.5 }]}>
              <Ionicons name="pause" size={12} color="rgba(255,255,255,0.4)" />
            </View>
            {collapseButton}
          </View>
          <View style={styles.skeletonWrapper} aria-busy="true" aria-label="Loading games">
            {[0, 1, 2, 3, 4].map((i) => (
              <SkeletonTickerItem key={i} index={i} />
            ))}
            {fades}
          </View>
        </TickerChrome>
      </View>
    );
  }

  // Show empty state only if not loading
  if (n === 0) {
    return (
      <View style={styles.container}>
        <TickerChrome>
          <View style={styles.controls}>{collapseButton}</View>
          <View style={styles.emptyState}>
            <Ionicons name="calendar-outline" size={16} color="rgba(255,255,255,0.4)" />
            <Text style={styles.emptyText}>No games available</Text>
          </View>
        </TickerChrome>
      </View>
    );
  }

  const Strip = IS_WEB ? View : Animated.View;

  return (
    <View style={styles.container}>
      <TickerChrome>
        <View style={styles.gradientBg} />

        {/* Left controls: loading dot, speed, collapse, previous league */}
        <View style={styles.controls}>
          {isLoadingMore && (
            <View style={styles.loadingMoreIndicator}>
              <View style={[styles.loadingDot, { opacity: 0.6 }]} />
            </View>
          )}

          {/* Speed toggle: 1x → 2x → 3x → paused */}
          <PressableScale
            onPress={cycleSpeed}
            style={styles.speedToggleBtn}
            hoverStyle={styles.controlBtnHover}
            hitSlop={{ top: 10, bottom: 10, left: 5, right: 5 }}
            role="button"
            aria-label={speedLabel}
          >
            {isPaused ? (
              <Ionicons name="play" size={11} color="rgba(255,255,255,0.85)" />
            ) : (
              <Text style={styles.speedToggleText}>{tickerSpeed}x</Text>
            )}
          </PressableScale>

          {collapseButton}

          {previousLeague && (
            <PressableScale
              onPress={skipToPreviousLeague}
              style={styles.prevLeagueBtn}
              hoverStyle={styles.leagueBtnHover}
              hitSlop={{ top: 10, bottom: 10, left: 5, right: 5 }}
              role="button"
              aria-label={`Back to ${previousLeague} games`}
            >
              <Ionicons name="chevron-back" size={13} color="#c4b5fd" />
              <Text style={styles.prevLeagueLabel}>{previousLeague}</Text>
            </PressableScale>
          )}
        </View>

        {/* Scrolling content */}
        <View
          ref={wrapperRef}
          style={styles.tickerWrapper}
          onLayout={(e) => setViewportWidth(e.nativeEvent.layout.width)}
          {...(IS_WEB ? {
            onMouseEnter: () => { hoverRef.current = true; },
            onMouseLeave: () => { hoverRef.current = false; },
            onTouchStart: () => { lastTouchRef.current = Date.now(); },
          } : {})}
        >
          <Strip
            ref={stripRef}
            style={[styles.tickerContent, IS_WEB ? styles.tickerContentWeb : { transform: [{ translateX: scrollX }] }]}
          >
            {rows}
          </Strip>
          {fades}
        </View>

        {/* Right controls: next league */}
        <View style={styles.controlsRight}>
          {nextLeague ? (
            <PressableScale
              onPress={skipToNextLeague}
              style={styles.nextLeagueBtn}
              hoverStyle={styles.leagueBtnHover}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              role="button"
              aria-label={`Skip to ${nextLeague} games`}
            >
              <Text style={styles.nextLeagueLabel}>{nextLeague}</Text>
              <Ionicons name="chevron-forward" size={13} color="#c4b5fd" />
            </PressableScale>
          ) : (
            <View style={styles.leagueIndicator}>
              <Text style={styles.currentLeagueText}>{sections[0] ? leagueLabel(sections[0].league) : ''}</Text>
            </View>
          )}
        </View>
      </TickerChrome>
      {card ? (() => {
        const f = card.fixture;
        const fid = f.fixture_id || f.id;
        const st = getGameStatus(f.start_date, referenceTime);
        const score = showLiveScores && liveScores ? liveScores[fid] : null;
        const isLive = st.status === 'live' || !!(score && score.is_live);
        return (
          <GamePopover
            id={CARD_ID}
            open
            fixture={f}
            anchorRect={card.rect}
            status={st}
            timeLabel={formatGameTime(f.start_date)}
            liveScore={score}
            periodLabel={score?.period ? formatPeriod(score.period, (f.league || '').toLowerCase()) : null}
            lines={!fid ? { status: 'ready', value: null } : lines.fixtureId === String(fid) ? lines : { status: 'loading' }}
            showSGP={sgpCacheStatus[fid] === true}
            showLive={isLive}
            onSearch={cardAction('game')}
            onBuildSGP={cardAction('sgp')}
            onLiveOdds={cardAction('live')}
            onMouseEnter={keepCardOpen}
            onMouseLeave={scheduleCardClose}
            onFocus={keepCardOpen}
            onBlur={onCardContainerBlur}
            onKeyDown={onCardKeyDown}
            cardRef={cardElRef}
            firstActionRef={firstActionRef}
          />
        );
      })() : null}
    </View>
  );
};


export default GameTicker;
